import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiDownload,
  FiCheck,
  FiAlertCircle,
  FiCheckCircle,
  FiArrowRight,
  FiShield,
  FiTrendingDown,
  FiHeart,
  FiMapPin,
  FiActivity,
  FiTrendingUp,
} from 'react-icons/fi';
import {
  BsShieldCheck,
  BsHeart,
  BsExclamationTriangle,
} from 'react-icons/bs';
import { 
  GiWheat, 
  GiChemicalDrop, 
  GiPoisonBottle, 
  GiPlantWatering,
  GiHealthNormal,
  GiPlantRoots,
} from 'react-icons/gi';
import InquiryModal from '../components/InquiryModal';
import gaajarghassImage from '../assets/products/gaajarghass.png';
import vMuktiBrochure from '../assets/pdf/Pamphletmukhti.pdf';
import ContactFormModal from '../components/ContactFormModal';

const ProductVMUKTI24 = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [downloadType, setDownloadType] = useState('');
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleDownloadClick = (type) => {
    setDownloadType(type);
    setModalOpen(true);
  };

  const nationalMenacePoints = [
    {
      title: 'Agricultural Loss',
      desc: 'Aggressively invades farmland, outcompetes essential crops, and significantly reduces overall agricultural yield',
      icon: GiWheat,
      color: 'from-red-500 to-orange-500',
    },
    {
      title: 'Severe Health Risk',
      desc: 'Highly toxic, causing severe health issues in both humans and livestock, including dermatitis and asthma in those exposed',
      icon: GiHealthNormal,
      color: 'from-pink-500 to-red-500',
    },
    {
      title: 'Ecological Damage',
      desc: 'Degrades biodiversity and renders land unusable, actively working against goals for a healthier environment',
      icon: FiAlertCircle,
      color: 'from-orange-500 to-amber-500',
    },
  ];

  const whyUnmatched = [
    {
      title: 'Water Base Efficacy',
      desc: 'Formulated for targeted action against Parthenium, ensuring eradication while protecting the surrounding ecosystem',
      icon: GiChemicalDrop,
      gradient: 'from-green-500 to-emerald-500',
    },
    {
      title: 'Eco-Friendly & Safe',
      desc: 'Non-toxic and harmless to human beings, plants, and the environment',
      icon: BsShieldCheck,
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      title: 'Swachh Bharat Commitment',
      desc: 'A concrete step towards reclaiming land and supporting the vision of a clean and healthy nation',
      icon: GiPlantRoots,
      gradient: 'from-emerald-500 to-green-600',
    },
  ];

  const benefitsData = [
    {
      title: 'Complete Eradication',
      desc: 'Provides effective control over the aggressive spread of Parthenium',
      icon: FiCheckCircle,
      color: 'text-green-600',
      bgColor: 'from-green-500 to-emerald-500',
    },
    {
      title: 'Reclaims Valuable Land',
      desc: 'Allows agricultural land, public spaces, and parks to be safely utilized again',
      icon: FiTrendingUp,
      color: 'text-blue-600',
      bgColor: 'from-blue-500 to-cyan-500',
    },
    {
      title: 'Protects Public Health',
      desc: 'Eliminates the source of severe allergens and toxins, safeguarding the health of farm workers, livestock, and residents',
      icon: BsHeart,
      color: 'text-pink-600',
      bgColor: 'from-pink-500 to-red-500',
    },
  ];

  const areasProtected = [
    { title: 'Agricultural Fields', icon: GiWheat },
    { title: 'Pastures and Grazing Land', icon: GiPlantWatering },
    { title: 'Roadsides and Highways', icon: FiMapPin },
    { title: 'Residential Areas and Public Parks', icon: BsShieldCheck },
  ];

  // Molecular pattern
  const MolecularPattern = () => (
    <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="molecule-vmukti" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
          <circle cx="20" cy="20" r="3" fill="currentColor" opacity="0.3" />
          <circle cx="80" cy="40" r="4" fill="currentColor" opacity="0.2" />
          <circle cx="140" cy="70" r="2" fill="currentColor" opacity="0.4" />
          <line x1="20" y1="20" x2="80" y2="40" stroke="currentColor" strokeWidth="0.5" opacity="0.2" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#molecule-vmukti)" />
    </svg>
  );

  return (
    <div className="min-h-screen overflow-hidden bg-white">
      {/* Section 1: Hero Banner & Core Promise */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-32">
        {/* Gaajarghas Background Image */}
        <div className="absolute inset-0">
          <img
            src={gaajarghassImage}
            alt="Parthenium Gaajarghas"
            className="w-full h-full object-cover"
          />
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/65 to-black/70" />
        </div>

        {/* Molecular Pattern */}
        <div className="absolute inset-0 text-emerald-400 opacity-10">
          <MolecularPattern />
        </div>

        {/* Floating leaf elements */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -70, 0],
                x: [0, Math.random() * 50 - 25, 0],
                rotate: [0, 360, 0],
                scale: [1, 1.5, 1],
                opacity: [0.3, 0.7, 0.3],
              }}
              transition={{
                duration: 8 + Math.random() * 4,
                repeat: Infinity,
                delay: Math.random() * 3,
              }}
            >
              <GiPlantRoots className="w-8 h-8 text-emerald-400/50" />
            </motion.div>
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 lg:px-8">
          <motion.div
            className="max-w-6xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge */}
            <motion.div
              className="inline-flex items-center space-x-3 bg-white/90 backdrop-blur-xl px-6 py-3 rounded-full mb-8 border-2 border-emerald-400/50 shadow-xl"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <GiPlantRoots className="text-emerald-600 text-2xl animate-pulse" />
              <span className="font-semibold text-emerald-900">Parthenium Eradication Solution</span>
            </motion.div>

            {/* Main Headline */}
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black mb-6 leading-tight drop-shadow-2xl">
              <span className="block text-white">
                V-MUKTI 24
              </span>
              <span className="block text-3xl md:text-4xl lg:text-5xl mt-4 text-white font-bold">
                The Only Water Base Solution
              </span>
              <span className="block text-4xl md:text-5xl lg:text-6xl text-emerald-300 font-bold">
                for Parthenium Eradication
              </span>
            </h1>

            {/* Sub-headline */}
            <motion.p
              className="text-xl md:text-2xl lg:text-3xl text-white mb-12 max-w-5xl mx-auto leading-relaxed drop-shadow-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              Reclaim your land and protect health with the safe, effective way to eliminate{' '}
              <span className="text-emerald-300 font-bold">Parthenium Hysterophorus</span> (Gajar Ghas).
            </motion.p>

            {/* CTAs */}
            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <button
                onClick={() => setContactModalOpen(true)}
                className="group px-12 py-6 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-bold text-xl rounded-full hover:shadow-2xl hover:shadow-emerald-500/50 transform hover:scale-105 transition-all duration-300 flex items-center space-x-3"
              >
                <span>Request a Quote for Large-Scale Application</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </button>

              <button
                onClick={() => handleDownloadClick('datasheet')}
                className="group px-12 py-6 bg-white/90 backdrop-blur-md text-slate-900 font-bold text-xl rounded-full border-2 border-emerald-300 hover:bg-white hover:text-green-900 transition-all duration-300 flex items-center space-x-3"
              >
                <FiDownload className="text-2xl" />
                <span>Download Product Data Sheet</span>
              </button>
            </motion.div>

            {/* Trust indicators */}
            <motion.div
              className="mt-16 flex flex-wrap items-center justify-center gap-8 text-sm text-white font-semibold drop-shadow-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              <div className="flex items-center space-x-2">
                <FiCheckCircle className="text-emerald-300" />
                <span>100% Water Base</span>
              </div>
              <div className="flex items-center space-x-2">
                <FiCheckCircle className="text-emerald-300" />
                <span>Eco-Friendly</span>
              </div>
              <div className="flex items-center space-x-2">
                <FiCheckCircle className="text-emerald-300" />
                <span>Safe for Environment</span>
              </div>
              <div className="flex items-center space-x-2">
                <FiCheckCircle className="text-emerald-300" />
                <span>Targeted Eradication</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-8 h-14 border-2 border-white/80 rounded-full flex justify-center p-2">
            <motion.div 
              className="w-2 h-4 bg-gradient-to-b from-emerald-300 to-green-300 rounded-full"
              animate={{ y: [0, 20, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>

      {/* Section 2: The National Menace of Parthenium */}
      <section id="menace" className="py-24 md:py-32 bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-200 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-200 rounded-full blur-3xl" />
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.div
                className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6 border-4 border-red-200"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <BsExclamationTriangle className="w-10 h-10 text-red-600" />
              </motion.div>

              <h2 className="text-5xl md:text-6xl font-black text-slate-900 mb-6">
                The National{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-600">
                  Menace of Parthenium
                </span>
              </h2>
              
              <p className="text-xl md:text-2xl text-slate-900 max-w-5xl mx-auto leading-relaxed">
                <span className="font-bold text-red-600">Parthenium Hysterophorus</span>, commonly known as{' '}
                <span className="font-semibold">Carrot Weed or Gajar Ghas</span>, is far more than just a weed; it is a{' '}
                <span className="font-bold text-red-600">major national environmental and health catastrophe</span>. Its
                rapid spread causes devastating consequences:
              </p>
            </div>

            {/* Menace Points */}
            <div className="grid md:grid-cols-3 gap-8 mb-16 max-w-6xl mx-auto">
              {nationalMenacePoints.map((point, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  whileHover={{ y: -10, scale: 1.03 }}
                  className="group relative"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${point.color} opacity-10 group-hover:opacity-20 blur-xl transition-opacity duration-500 rounded-3xl`} />
                  
                  <div className="relative bg-white rounded-3xl p-8 shadow-xl border-2 border-red-200 hover:border-red-400 transition-all duration-300">
                    <motion.div
                      className={`w-16 h-16 bg-gradient-to-br ${point.color} rounded-2xl flex items-center justify-center mb-6`}
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                    >
                      <point.icon className="w-8 h-8 text-white" />
                    </motion.div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">{point.title}</h3>
                    <p className="text-slate-900 leading-relaxed text-lg">{point.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <a
                href="#solution"
                className="inline-flex items-center space-x-3 px-10 py-5 bg-gradient-to-r from-red-600 to-orange-600 text-white font-bold text-lg rounded-full hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                <GiPlantRoots className="text-2xl" />
                <span>Join the Movement for a Clean India</span>
                <FiArrowRight className="text-2xl" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Section 3: The VP Chem Technologies Solution */}
      <section id="solution" className="py-24 md:py-32 bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 relative overflow-hidden">
        {/* Animated grid */}
        <div className="absolute inset-0 opacity-5">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, rgba(16, 185, 129, 0.3) 1px, transparent 1px),
                                linear-gradient(to bottom, rgba(16, 185, 129, 0.3) 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
            }}
          />
        </div>

        {/* Floating molecules */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -50, 0],
                opacity: [0.1, 0.3, 0.1],
              }}
              transition={{
                duration: 8 + Math.random() * 4,
                repeat: Infinity,
                delay: Math.random() * 5,
              }}
            >
              <div className="w-3 h-3 bg-emerald-400 rounded-full" />
            </motion.div>
          ))}
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.span
                className="inline-block px-6 py-2 bg-emerald-500/20 backdrop-blur-xl rounded-full text-emerald-400 font-semibold mb-6 border border-emerald-400/30"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring" }}
              >
                🌿 The VP Chem Technologies Solution
              </motion.span>
              
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-8">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-green-600">
                  V-MUKTI 24
                </span>
                <br />
                <span className="text-slate-900 text-3xl md:text-4xl mt-4 block">
                  Revolutionary Water Base Solution
                </span>
              </h2>
              
              <p className="text-xl md:text-2xl text-slate-900 max-w-4xl mx-auto leading-relaxed mb-8">
                V-MUKTI 24 is revolutionary because it is the{' '}
                <span className="text-emerald-700 font-bold text-3xl">ONLY</span>{' '}
                <span className="text-emerald-700 font-bold">Water Base Solution</span> developed specifically for
                the eradication of{' '}
                <span className="text-emerald-700 font-semibold">Parthenium Hysterophorus</span>. This product provides
                a safe, highly effective alternative to traditional, often ineffective, water base herbicides.
              </p>
            </div>

            {/* Why Unmatched Cards */}
            <div className="max-w-6xl mx-auto mb-12">
              <h3 className="text-4xl font-black text-center text-slate-900 mb-12">
                Why V-MUKTI 24 is Unmatched
              </h3>

              <div className="grid md:grid-cols-3 gap-8">
                {whyUnmatched.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15 }}
                    whileHover={{ y: -10 }}
                    className="group relative"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 rounded-3xl`} />
                    
                    <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 h-full group-hover:bg-white/10 group-hover:border-white/30 transition-all duration-500">
                      <motion.div
                        className={`w-20 h-20 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center mb-6`}
                        whileHover={{ rotate: 360, scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                      >
                        <item.icon className="w-10 h-10 text-white" />
                      </motion.div>
                      
                      <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                        {item.title}
                      </h3>
                      <p className="text-slate-900 leading-relaxed text-lg">
                        {item.desc}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Swachh Bharat Banner */}
            <motion.div
              className="max-w-5xl mx-auto bg-gradient-to-r from-orange-500/20 to-green-500/20 backdrop-blur-xl border-2 border-emerald-400/30 rounded-3xl p-10 mb-16"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <div className="text-center">
                <motion.div
                  className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500 to-green-500 rounded-full mb-6"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                >
                  <GiPlantRoots className="w-10 h-10 text-white" />
                </motion.div>
                
                <h3 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
                  Swachh Bharat: Ek Kadam Swachhata Ki Or
                </h3>
                <p className="text-xl text-slate-900 leading-relaxed">
                  Using V-MUKTI 24 is a concrete commitment to reclaiming land and supporting the vision of a{' '}
                  <span className="text-emerald-700 font-bold">clean and healthy nation</span>.
                </p>
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <button
                onClick={() => handleDownloadClick('technical-brochure')}
                className="group inline-flex items-center space-x-3 px-10 py-5 bg-white text-green-900 font-bold text-lg rounded-full hover:bg-emerald-100 transform hover:scale-105 transition-all duration-300 shadow-2xl"
              >
                <FiDownload className="text-2xl" />
                <span>Download V-MUKTI 24 Technical Brochure</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Section 4: Benefits for Land and Health */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-emerald-50 via-green-50 to-emerald-50 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1/2 h-1/2 bg-gradient-to-br from-emerald-100/50 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-gradient-to-br from-green-100/50 to-transparent rounded-full blur-3xl" />
        
        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.span
                className="inline-block px-6 py-2 bg-gradient-to-r from-emerald-100 to-green-100 rounded-full text-emerald-700 font-semibold mb-6"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring" }}
              >
                🌱 Long-Term Impact
              </motion.span>
              
              <h2 className="text-5xl md:text-6xl font-black text-slate-900 mb-6">
                Benefits for{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-green-600">
                  Land and Health
                </span>
              </h2>
              
              <p className="text-xl md:text-2xl text-slate-900 max-w-4xl mx-auto leading-relaxed">
                By utilizing V-MUKTI 24, you achieve{' '}
                <span className="font-bold text-emerald-600">immediate eradication</span> and contribute to the
                long-term health and prosperity of your community and environment.
              </p>
            </div>

            {/* Benefits Grid */}
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
              {benefitsData.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  whileHover={{ y: -10, scale: 1.03 }}
                  className="group relative"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${benefit.bgColor} opacity-10 group-hover:opacity-20 blur-xl transition-opacity duration-500 rounded-3xl`} />
                  
                  <div className="relative bg-white rounded-3xl p-8 shadow-xl border-2 border-emerald-200 hover:border-emerald-400 transition-all duration-300">
                    <motion.div
                      className={`w-20 h-20 bg-gradient-to-br ${benefit.bgColor} rounded-2xl flex items-center justify-center mb-6`}
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                    >
                      <benefit.icon className="w-10 h-10 text-white" />
                    </motion.div>
                    
                    <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                      {benefit.title}
                    </h3>
                    <p className="text-slate-900 leading-relaxed text-lg">
                      {benefit.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Areas Benefiting */}
            <div className="max-w-5xl mx-auto">
              <h3 className="text-4xl font-black text-center text-slate-900 mb-12">
                Areas Benefiting from{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-green-600">
                  V-MUKTI 24
                </span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {areasProtected.map((area, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                    className="bg-white rounded-2xl p-8 shadow-xl border-2 border-emerald-200 hover:border-emerald-400 transition-all duration-300"
                  >
                    <div className="flex items-center space-x-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <area.icon className="w-8 h-8 text-white" />
                      </div>
                      <h4 className="text-2xl font-bold text-slate-900">{area.title}</h4>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <motion.div
              className="text-center mt-16"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <Link
                to="/contact"
                className="inline-flex items-center space-x-3 px-10 py-5 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-bold text-lg rounded-full hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                <FiActivity className="text-2xl" />
                <span>Get Consultation on Weed Management</span>
                <FiArrowRight className="text-2xl" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-r from-emerald-600 via-green-600 to-emerald-600 relative overflow-hidden">
        {/* Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -100, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 3,
              }}
            />
          ))}
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            className="max-w-5xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-8">
              Reclaim Your Land. Protect Your Health.
            </h2>
            
            <p className="text-xl md:text-2xl text-white mb-12 max-w-3xl mx-auto">
              Join the movement for a cleaner, healthier India with V-MUKTI 24
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link
                to="/contact"
                className="group w-full sm:w-auto px-12 py-6 bg-white text-emerald-600 font-bold text-xl rounded-2xl hover:bg-slate-100 transform hover:-translate-y-2 hover:scale-105 transition-all duration-300 shadow-2xl flex items-center justify-center space-x-3"
              >
                <span>Start Your Eradication Program</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </Link>
              
              <button
                onClick={() => handleDownloadClick('brochure')}
                className="group w-full sm:w-auto px-12 py-6 bg-transparent border-3 border-white text-white font-bold text-xl rounded-2xl hover:bg-white hover:text-emerald-600 transform hover:-translate-y-2 hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-3"
              >
                <FiDownload className="text-2xl" />
                <span>Download Complete Guide</span>
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Inquiry Modal */}
      <InquiryModal 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)}
        downloadType={downloadType}
        productName="V-MUKTI 24"
        downloadUrl={vMuktiBrochure}
      />
      
      {/* Contact Form Modal */}
      <ContactFormModal 
        isOpen={contactModalOpen} 
        onClose={() => setContactModalOpen(false)}
        productName="V-MUKTI 24"
      />
    </div>
  );
};

export default ProductVMUKTI24;



